import { type FC, useState } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	AlertFillCircleIcon,
	CheckCircleIcon,
	XCircleIcon
} from '@/components/ui/icons'
import { cn } from '@/helpers/utils'
import { type TKey, useTranslation } from '@/translations/i18n'
import {
	BLOCKING_CRITERIA_COUNT,
	type FinalScoreEligibility,
	formatFailedCriteria,
	getScoreTextColor,
	LUXE_PASS_MARK,
	resolveFinalScoreEligibility
} from '../utils'

const eligibilityToStyles = {
	eligible: {
		scoreColor: 'text-success-600',
		iconWrapper: 'bg-success-100 text-success-600',
		labelKey: 'luxe_submissions:final_score_eligible'
	},
	not_eligible: {
		scoreColor: 'text-danger-600',
		iconWrapper: 'bg-danger-100 text-danger-600',
		labelKey: 'luxe_submissions:final_score_not_eligible'
	}
} as const satisfies Record<
	FinalScoreEligibility,
	{ scoreColor: string; iconWrapper: string; labelKey: TKey }
>

const passStyles = {
	pass: 'bg-green-300 text-green-800',
	fail: 'bg-danger-100 text-danger-600'
}

interface BreakdownRowProps {
	index: number
	label: string
	value: string
	valueColor?: string
	statusLabel: string
	isPassing: boolean
	description?: string
}

const BreakdownRow: FC<BreakdownRowProps> = ({
	index,
	label,
	value,
	valueColor,
	statusLabel,
	isPassing,
	description
}) => (
	<div className='flex flex-col gap-1 rounded-lg bg-grey-100 p-4'>
		<div className='flex items-start justify-between gap-3'>
			<p className='font-semibold text-neutral-700 text-xs uppercase'>
				{index}. {label}
			</p>
			<span
				className={cn(
					'shrink-0 rounded-md px-2 py-0.5 font-medium text-sm',
					isPassing ? passStyles.pass : passStyles.fail
				)}
			>
				{statusLabel}
			</span>
		</div>
		<p
			className={cn(
				'font-bold text-display-xs-fixed',
				valueColor ?? 'text-neutral-900'
			)}
		>
			{value}
		</p>
		{description && (
			<p className='text-neutral-700 text-sm'>{description}</p>
		)}
	</div>
)

interface FinalScoreCardProps {
	score: number
	designScore: number | null
	blockersFailedCount: number
	/** Codes of the failed blocking criteria — table stakes and blockers alike. */
	failedBlockers: string[]
}

/**
 * The Final Score and what produced it. The score on its own reads as a design
 * score, which is what drove the confusion this card resolves: the outcome is
 * decided by the design score cut by 30% per failed blocking criterion, so the
 * breakdown underneath names both halves — the design score, which never
 * decides the outcome on its own, and how many blockers failed.
 */
export const FinalScoreCard: FC<FinalScoreCardProps> = ({
	score,
	designScore,
	blockersFailedCount,
	failedBlockers
}) => {
	const { t } = useTranslation()
	const [areDetailsVisible, setAreDetailsVisible] = useState(false)

	const eligibility = resolveFinalScoreEligibility(score)
	const eligibilityStyles = eligibilityToStyles[eligibility]
	const isEligible = eligibility === 'eligible'

	const failedText = formatFailedCriteria(failedBlockers, t)
	// The banner names each criterion once, so it counts them the same way.
	const failedCodes = new Set(
		failedBlockers.map((code) => code.toLowerCase())
	)
	// Blockers (B1-B3) travel in the same list as table stakes, and calling those
	// a table stake failure would be wrong — so the wording widens once one of
	// them is in there.
	const areAllTableStakes = [...failedCodes].every((code) =>
		code.startsWith('ts')
	)
	const hasNoFailedBlockers = blockersFailedCount === 0

	return (
		<div className='flex flex-col rounded-lg border border-neutral-300'>
			<div className='flex flex-col gap-4 p-4'>
				<div className='flex items-start justify-between gap-3'>
					<p className='font-semibold text-neutral-700 text-xs uppercase'>
						{t('luxe_submissions:final_score_label')}
					</p>
					<div
						className={cn(
							'flex size-8 shrink-0 items-center justify-center rounded-full',
							eligibilityStyles.iconWrapper
						)}
					>
						{isEligible ? (
							<CheckCircleIcon size={16} />
						) : (
							<XCircleIcon size={16} />
						)}
					</div>
				</div>

				<div className='flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1'>
					<p
						className={cn(
							'font-bold text-display-sm-fixed',
							eligibilityStyles.scoreColor
						)}
					>
						{score}
						<span className='font-normal text-neutral-700 text-sm'>
							/100
						</span>
					</p>
					<span className='font-semibold text-base text-neutral-900'>
						{t(eligibilityStyles.labelKey)}
					</span>
				</div>

				{failedText && (
					<div className='flex items-center gap-3 bg-neutral-100 p-4'>
						<AlertFillCircleIcon
							className='shrink-0 text-danger-600'
							size={20}
						/>
						<p className='font-semibold text-grey-1000 text-sm'>
							{t(
								areAllTableStakes
									? 'luxe_submissions:final_score_table_stakes_failure'
									: 'luxe_submissions:final_score_criteria_failure',
								{
									criteria: failedText,
									count: failedCodes.size
								}
							)}
						</p>
					</div>
				)}

				<ButtonLegacy
					className='self-start'
					onClick={() => setAreDetailsVisible((visible) => !visible)}
					shape='rounded'
					size='sm'
					type='button'
					variant='outline'
				>
					{t(
						areDetailsVisible
							? 'luxe_submissions:final_score_hide_details'
							: 'luxe_submissions:final_score_show_details'
					)}
				</ButtonLegacy>
			</div>

			{areDetailsVisible && (
				<div className='flex flex-col gap-2 border-neutral-300 border-t p-4'>
					<BreakdownRow
						description={t(
							'luxe_submissions:final_score_design_description'
						)}
						index={1}
						isPassing={
							designScore != null && designScore >= LUXE_PASS_MARK
						}
						label={t('luxe_submissions:final_score_design_label')}
						statusLabel={t(
							designScore != null && designScore >= LUXE_PASS_MARK
								? 'luxe_submissions:final_score_design_meets'
								: 'luxe_submissions:final_score_design_not_meets'
						)}
						value={designScore == null ? '—' : `${designScore}`}
						valueColor={getScoreTextColor(designScore)}
					/>

					<BreakdownRow
						index={2}
						isPassing={hasNoFailedBlockers}
						label={t('luxe_submissions:final_score_blockers_label')}
						statusLabel={t(
							hasNoFailedBlockers
								? 'luxe_submissions:final_score_blockers_none_failed'
								: 'luxe_submissions:submit_confirm_not_qualified'
						)}
						value={`${blockersFailedCount}/${BLOCKING_CRITERIA_COUNT}`}
					/>
				</div>
			)}
		</div>
	)
}
